import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardList, Loader2, Download, CloudSun, Save, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { generateFlightBriefing, listBriefingPresets, saveBriefingPreset, deleteBriefingPreset, getFlightAlternates } from "@/lib/api";

const IPPC_CHARTS_URL = "https://www.ippc.no/ippc/index.jsp";

const FL_LEVELS = ["050", "100", "140", "180", "240", "300", "340", "390"];

// Fields saved in a preset (flight-specific aerodromes/alternates/validity are excluded).
const PRESET_KEYS = [
  "firs", "not_older_than_days", "types", "scope", "rules",
  "include_metar", "include_taf", "include_notams", "include_sigmet",
  "embed_chart", "include_sigwx", "include_surface_pressure", "chart_levels", "chart_time",
  "include_convective", "include_gramet", "include_airport_charts", "hide_expired",
];

// Add N days to a "YYYY-MM-DD" string, returning "YYYY-MM-DD".
const addDays = (dateStr, n) => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const b64ToBlobUrl = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const b64ToFile = (b64, filename) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: "application/pdf" });
};

const Check = ({ id, checked, onChange, label }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0f172a] dark:text-white" htmlFor={id}>
    <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={`briefing-chk-${id}`} />
    {label}
  </label>
);

const Section = ({ title, children }) => (
  <div className="rounded-lg border border-[#e2e8f0] dark:border-slate-700 p-3">
    <div className="text-xs font-semibold uppercase tracking-wide text-[#64748b] dark:text-gray-400 mb-2">{title}</div>
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

export function BriefingButton({ flight, variant = "icon" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const dep = (flight.origin || "").toUpperCase();
  const dest = (flight.destination || "").toUpperCase();

  const [form, setForm] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [presetPrivate, setPresetPrivate] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const handleOpen = () => {
    const depDate = flight.flight_date || "";
    setForm({
      aerodromes: [dep, dest].filter(Boolean).join(", "),
      alternates: "",
      firs: "",
      validity_from: depDate,
      validity_to: addDays(depDate, 2),
      not_older_than_days: 365,
      types: { international: true, national: false, military: true },
      scope: { aerodrome: true, enroute: true, warnings: true },
      rules: { ifr: true, vfr: true },
      include_metar: true,
      include_taf: true,
      include_notams: true,
      include_sigmet: true,
      embed_chart: true,
      include_sigwx: true,
      include_surface_pressure: true,
      include_convective: true,
      include_gramet: true,
      include_airport_charts: false,
      hide_expired: true,
      chart_levels: ["300"],
      chart_time: "",
    });
    setPresetId("");
    setPresetPrivate(false);
    setOpen(true);
    listBriefingPresets().then((r) => setPresets(r.data || [])).catch(() => {});
  };

  const applyPreset = (id) => {
    setPresetId(id);
    const p = presets.find((x) => x.id === id);
    if (!p || !p.config) return;
    const patch = {};
    PRESET_KEYS.forEach((k) => {
      if (p.config[k] !== undefined) patch[k] = p.config[k];
    });
    setForm((f) => ({ ...f, ...patch }));
    toast.success(`Loaded "${p.name}"`);
  };

  const handleSavePreset = async () => {
    const name = window.prompt("Save this briefing definition as:", presets.find((x) => x.id === presetId)?.name || "");
    if (!name || !name.trim()) return;
    const config = {};
    PRESET_KEYS.forEach((k) => { config[k] = form[k]; });
    try {
      const r = await saveBriefingPreset({ name: name.trim(), config, is_private: presetPrivate });
      const list = await listBriefingPresets();
      setPresets(list.data || []);
      setPresetId(r.data.id);
      toast.success(`Saved "${name.trim()}"${presetPrivate ? " (private)" : ""}`);
    } catch (e) {
      toast.error("Could not save the definition");
    }
  };

  const suggestAlternates = async () => {
    setSuggesting(true);
    try {
      const r = await getFlightAlternates(flight.id, 3);
      const list = (r.data.alternates || []).map((a) => a.icao);
      if (!list.length) {
        toast.info("No nearby alternates found");
        return;
      }
      setForm((f) => {
        const existing = f.alternates.split(",").map((s) => s.trim()).filter(Boolean);
        const merged = Array.from(new Set([...existing, ...list]));
        return { ...f, alternates: merged.join(", ") };
      });
      toast.success(`Added ${list.length} alternate(s): ${list.join(", ")}`);
    } catch (e) {
      toast.error("Could not fetch alternates");
    } finally {
      setSuggesting(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!presetId) return;
    try {
      await deleteBriefingPreset(presetId);
      setPresets((ps) => ps.filter((x) => x.id !== presetId));
      setPresetId("");
      toast.success("Definition deleted");
    } catch (e) {
      toast.error("Could not delete the definition");
    }
  };

  const setGroup = (group, key, val) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: val } }));

  const toggleLevel = (fl) =>
    setForm((f) => ({
      ...f,
      chart_levels: f.chart_levels.includes(fl)
        ? f.chart_levels.filter((x) => x !== fl)
        : [...f.chart_levels, fl],
    }));

  const buildPayload = () => ({
    aerodromes: form.aerodromes.split(",").map((s) => s.trim()).filter(Boolean),
    alternates: form.alternates.split(",").map((s) => s.trim()).filter(Boolean),
    firs: form.firs.split(",").map((s) => s.trim()).filter(Boolean),
    validity_from: form.validity_from,
    validity_to: form.validity_to,
    not_older_than_days: parseInt(form.not_older_than_days, 10) || 365,
    types: form.types,
    scope: form.scope,
    rules: form.rules,
    include_metar: form.include_metar,
    include_taf: form.include_taf,
    include_notams: form.include_notams,
    include_sigmet: form.include_sigmet,
    embed_chart: form.embed_chart,
    include_sigwx: form.include_sigwx,
    include_surface_pressure: form.include_surface_pressure,
    include_convective: form.include_convective,
    include_gramet: form.include_gramet,
    include_airport_charts: form.include_airport_charts,
    hide_expired: form.hide_expired,
    chart_levels: form.embed_chart ? FL_LEVELS.filter((l) => form.chart_levels.includes(l)) : [],
    chart_time: form.chart_time,
  });

  const handleGenerate = async () => {
    setSaving(true);
    try {
      const res = await generateFlightBriefing(flight.id, buildPayload());
      const url = b64ToBlobUrl(res.data.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.open(url, "_blank");
      toast.success("Crew Briefing generated & saved to history");
      setOpen(false);
    } catch (e) {
      toast.error("Could not generate the briefing");
    } finally {
      setSaving(false);
    }
  };

  const emailBriefing = async () => {
    setEmailing(true);
    let res;
    try {
      res = await generateFlightBriefing(flight.id, buildPayload());
    } catch (e) {
      toast.error("Could not generate the briefing");
      setEmailing(false);
      return;
    }
    try {
      const { data, filename } = res.data;
      const subject = `Crew Briefing — ${[flight.flight_number, `${dep}→${dest}`, flight.flight_date].filter(Boolean).join(" ")}`;
      const body =
        `Please find attached the crew pre-flight briefing.\n\n` +
        `Flight: ${flight.flight_number || "—"}\n` +
        `Route: ${dep || "—"} → ${dest || "—"}\n` +
        `Date: ${flight.flight_date || "—"}\n` +
        `STD (local): ${flight.time || "—"}\n` +
        `Registration: ${flight.registration || "—"}\n\n` +
        `Generated by BSCA Business Aviation.`;

      // Preferred: native share sheet WITH the PDF attached (mobile Outlook/Mail & supported desktops).
      // navigator.share needs a live user-gesture; after the async generation the gesture may have
      // expired (throws NotAllowedError) — in that case we fall back to download + mailto below.
      let shared = false;
      try {
        const file = b64ToFile(data, filename);
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: subject, text: body });
          shared = true;
          toast.success("Opening your mail / share app with the briefing attached…");
        }
      } catch (shareErr) {
        if (shareErr && shareErr.name === "AbortError") {
          setEmailing(false);
          setOpen(false);
          return; // user cancelled the share sheet — nothing more to do
        }
        // any other share error (gesture expired, unsupported target): fall through to mailto
      }

      if (!shared) {
        // Fallback: download the PDF and open the mail client with a pre-filled subject/body.
        const url = b64ToBlobUrl(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        const mailBody = body + `\n\nNOTE: please attach the downloaded file "${filename}" (mail apps can't auto-attach from the browser).`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailBody)}`, "_blank");
        toast.success(`Briefing downloaded — attach "${filename}" in the email that just opened`);
      }
      setOpen(false);
    } catch (e) {
      toast.error("Could not prepare the email");
    } finally {
      setEmailing(false);
    }
  };

  const inputCls = "h-8 text-sm bg-white dark:bg-[#0f172a]";

  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          className="text-[#64748b] hover:text-[#d4af37] hover:bg-[#d4af37]/10 h-8 w-8"
          title="Generate Crew Briefing"
          data-testid={`briefing-button-${flight.id}`}
        >
          <ClipboardList size={16} />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpen}
          className="flex items-center gap-2 text-[#0f172a] border-[#d4af37] hover:bg-[#d4af37] hover:text-white"
          data-testid={`briefing-button-${flight.id}`}
        >
          <ClipboardList size={14} />
          Briefing
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl bg-white dark:bg-[#1e293b] max-h-[88vh] overflow-y-auto"
          data-testid="briefing-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] text-[#0f172a] dark:text-white flex items-center gap-2">
              <ClipboardList size={22} className="text-[#d4af37]" />
              Crew Briefing
              <span className="text-sm font-normal text-[#64748b] dark:text-gray-400">
                {flight.flight_number} · {dep} → {dest}
              </span>
            </DialogTitle>
          </DialogHeader>

          {form && (
            <div className="space-y-3">
              <div className="flex items-end gap-2 rounded-lg border border-[#e2e8f0] dark:border-slate-700 p-2 bg-[#f8fafc] dark:bg-[#0f172a]/40">
                <div className="flex-1">
                  <label className="text-xs text-[#64748b] dark:text-gray-400">Saved definition</label>
                  <select
                    className="w-full h-8 text-sm rounded-md border border-input bg-white dark:bg-[#0f172a] px-2 text-[#0f172a] dark:text-white"
                    value={presetId}
                    onChange={(e) => applyPreset(e.target.value)}
                    data-testid="briefing-preset-select"
                  >
                    <option value="">— Select a saved definition —</option>
                    {presets.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <Button variant="outline" size="sm" onClick={handleSavePreset}
                  className="flex items-center gap-1" data-testid="briefing-preset-save">
                  <Save size={14} /> Save
                </Button>
                <Button variant="outline" size="sm" onClick={handleDeletePreset} disabled={!presetId}
                  className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40"
                  data-testid="briefing-preset-delete">
                  <Trash2 size={14} /> Delete
                </Button>
              </div>
              <label className="flex items-center gap-2 -mt-1 text-xs text-[#64748b] dark:text-gray-400 cursor-pointer" htmlFor="preset-private">
                <Checkbox id="preset-private" checked={presetPrivate} onCheckedChange={setPresetPrivate} data-testid="briefing-preset-private" />
                Save as private (only visible to me)
              </label>
              <div>
                <label className="text-xs text-[#64748b] dark:text-gray-400">Aerodromes (comma separated)</label>
                <Input
                  className={inputCls}
                  value={form.aerodromes}
                  onChange={(e) => setForm({ ...form, aerodromes: e.target.value })}
                  data-testid="briefing-aerodromes"
                />
              </div>
              <div>
                <label className="text-xs text-[#64748b] dark:text-gray-400">FIRs (comma separated)</label>
                <Input
                  className={inputCls}
                  value={form.firs}
                  onChange={(e) => setForm({ ...form, firs: e.target.value })}
                  data-testid="briefing-firs"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-[#64748b] dark:text-gray-400">Alternate(s) (comma separated, optional)</label>
                  <button
                    type="button"
                    onClick={suggestAlternates}
                    disabled={suggesting}
                    className="text-[11px] text-[#d4af37] hover:underline disabled:opacity-50"
                    data-testid="briefing-suggest-alternates"
                  >
                    {suggesting ? "Finding…" : "Suggest nearby"}
                  </button>
                </div>
                <Input
                  className={inputCls}
                  value={form.alternates}
                  onChange={(e) => setForm({ ...form, alternates: e.target.value })}
                  placeholder="e.g. EBBR, EBLG"
                  data-testid="briefing-alternates"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Section title="NOTAM Validity">
                  <label className="text-[11px] text-[#64748b] dark:text-gray-400">From</label>
                  <Input className={inputCls} type="date" value={form.validity_from}
                    onChange={(e) => setForm({ ...form, validity_from: e.target.value })} data-testid="briefing-validity-from" />
                  <label className="text-[11px] text-[#64748b] dark:text-gray-400">To</label>
                  <Input className={inputCls} type="date" value={form.validity_to}
                    onChange={(e) => setForm({ ...form, validity_to: e.target.value })} data-testid="briefing-validity-to" />
                </Section>

                <Section title="Type">
                  <Check id="international" checked={form.types.international} onChange={(v) => setGroup("types", "international", v)} label="International" />
                  <Check id="national" checked={form.types.national} onChange={(v) => setGroup("types", "national", v)} label="National" />
                  <Check id="military" checked={form.types.military} onChange={(v) => setGroup("types", "military", v)} label="Military" />
                </Section>

                <Section title="Scope">
                  <Check id="aerodrome" checked={form.scope.aerodrome} onChange={(v) => setGroup("scope", "aerodrome", v)} label="Aerodrome" />
                  <Check id="enroute" checked={form.scope.enroute} onChange={(v) => setGroup("scope", "enroute", v)} label="En-route" />
                  <Check id="warnings" checked={form.scope.warnings} onChange={(v) => setGroup("scope", "warnings", v)} label="Warnings" />
                  <Check id="hide-expired" checked={form.hide_expired} onChange={(v) => setForm({ ...form, hide_expired: v })} label="Hide expired NOTAMs" />
                </Section>

                <Section title="Rules & Weather">
                  <Check id="ifr" checked={form.rules.ifr} onChange={(v) => setGroup("rules", "ifr", v)} label="IFR" />
                  <Check id="vfr" checked={form.rules.vfr} onChange={(v) => setGroup("rules", "vfr", v)} label="VFR" />
                  <Check id="metar" checked={form.include_metar} onChange={(v) => setForm({ ...form, include_metar: v })} label="METAR" />
                  <Check id="taf" checked={form.include_taf} onChange={(v) => setForm({ ...form, include_taf: v })} label="TAF" />
                </Section>
              </div>

              <div className="rounded-lg border border-[#e2e8f0] dark:border-slate-700 p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-[#64748b] dark:text-gray-400 mb-2">
                  Include in briefing
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <Check id="notams" checked={form.include_notams} onChange={(v) => setForm({ ...form, include_notams: v })} label="NOTAMs (Dep/En-route/Dest)" />
                  <Check id="sigmet" checked={form.include_sigmet} onChange={(v) => setForm({ ...form, include_sigmet: v })} label="SIGMETs / AIRMETs" />
                  <Check id="surface" checked={form.include_surface_pressure} onChange={(v) => setForm({ ...form, include_surface_pressure: v })} label="Surface Pressure" />
                  <Check id="sigwx" checked={form.include_sigwx} onChange={(v) => setForm({ ...form, include_sigwx: v })} label="Significant Wx (SIGWX)" />
                  <Check id="convective" checked={form.include_convective} onChange={(v) => setForm({ ...form, include_convective: v })} label="Convective (CB/CAPE)" />
                  <Check id="chart" checked={form.embed_chart} onChange={(v) => setForm({ ...form, embed_chart: v })} label="Upper Wind & Temp" />
                  <Check id="gramet" checked={form.include_gramet} onChange={(v) => setForm({ ...form, include_gramet: v })} label="GRAMET (route cross-section)" />
                  <Check id="airport-charts" checked={form.include_airport_charts} onChange={(v) => setForm({ ...form, include_airport_charts: v })} label="Airport Charts (AIP)" />
                </div>
              </div>

              {form.embed_chart && (
                <Section title="Upper Wind & Temperature — Flight Levels (Europe)">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-[#64748b] dark:text-gray-400">
                      Each selected level is added as its own chart page ({form.chart_levels.length}/{FL_LEVELS.length} selected). Charts cover the EBCI→destination route.
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-[#64748b] dark:text-gray-400">Valid</span>
                      <select
                        className="h-7 text-xs rounded-md border border-input bg-white dark:bg-[#0f172a] px-1 text-[#0f172a] dark:text-white"
                        value={form.chart_time}
                        onChange={(e) => setForm({ ...form, chart_time: e.target.value })}
                        data-testid="briefing-chart-time"
                      >
                        <option value="">Auto</option>
                        <option value="0000">00 UTC</option>
                        <option value="0600">06 UTC</option>
                        <option value="1200">12 UTC</option>
                        <option value="1800">18 UTC</option>
                      </select>
                      <button
                        type="button"
                        className="text-[11px] text-[#d4af37] hover:underline"
                        onClick={() => setForm({ ...form, chart_levels: [...FL_LEVELS] })}
                        data-testid="briefing-fl-all"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className="text-[11px] text-[#64748b] hover:underline"
                        onClick={() => setForm({ ...form, chart_levels: [] })}
                        data-testid="briefing-fl-none"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {FL_LEVELS.map((fl) => {
                      const active = form.chart_levels.includes(fl);
                      return (
                        <button
                          key={fl}
                          type="button"
                          onClick={() => toggleLevel(fl)}
                          className={
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors " +
                            (active
                              ? "bg-[#d4af37] text-[#0f172a] border-[#d4af37]"
                              : "bg-transparent text-[#64748b] dark:text-gray-300 border-[#e2e8f0] dark:border-slate-600 hover:border-[#d4af37]")
                          }
                          data-testid={`briefing-fl-${fl}`}
                        >
                          FL{fl}
                        </button>
                      );
                    })}
                  </div>
                </Section>
              )}

              <p className="text-xs text-[#64748b] dark:text-gray-400">
                Times are UTC. This briefing is advisory — always verify with official AIS before flight.
              </p>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(IPPC_CHARTS_URL, "_blank")}
              className="flex items-center gap-2 sm:mr-auto"
              data-testid="briefing-weather-charts"
            >
              <CloudSun size={16} />
              Weather Charts
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="briefing-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={saving || emailing}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] flex items-center gap-2"
              data-testid="briefing-generate"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Get Briefing (PDF)
            </Button>
            <Button
              onClick={emailBriefing}
              disabled={saving || emailing}
              variant="outline"
              className="border-[#d4af37] text-[#0f172a] dark:text-white hover:bg-[#d4af37]/10 flex items-center gap-2"
              data-testid="briefing-email"
            >
              {emailing ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              Email Briefing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
